import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Chip } from "@amzn/stencil-react-components/chip";
import { Flyout, FlyoutContent, WithFlyout } from "@amzn/stencil-react-components/flyout";
import { TokenType, useRecommendedLength } from "@amzn/stencil-react-components/hooks";
import { Input, InputWrapper, RecommendedLengthFooter } from "@amzn/stencil-react-components/input";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { StarRating } from "@amzn/stencil-react-components/star-rating";
import { Label, Text } from "@amzn/stencil-react-components/text";
import { TextArea } from "@amzn/stencil-react-components/text-area";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { useEffect, useState } from "react";
import { FunctionComponent } from "react";

export interface SubmitReviewFormProps {
  titleText: string;
  productName: string;
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  previousRating?: number;
}

const CATEGORIES = ["Features", "Accuracy", "Performance", "Integration", "Usability", "Others"];

const REVIEW_CONTENT_WORD_LIMIT = 150;
const REVIEW_TITLE_WORD_LIMIT = 20;

export const SubmitReviewForm: FunctionComponent<SubmitReviewFormProps> = ({
  titleText,
  productName,
  isOpen,
  setIsOpen,
  previousRating,
}) => {
  const [enableConfirmButton, setEnableConfirmButton] = useState(false);
  const [reviewContentError, setReviewContentError] = useState(false);
  const [reviewTitleError, setReviewTitleError] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
  const [rating, setRating] = useState<number>(0);
  const [reviewTitle, setReviewTitle] = useState<string>("");
  const [reviewContent, setReviewContent] = useState<string>("");

  const {
    footerText: reviewContentFooterText,
    progress: reviewContentProgress,
    count: reviewContentCount,
  } = useRecommendedLength({
    value: reviewContent,
    tokenType: TokenType.Words,
    recommendedLength: REVIEW_CONTENT_WORD_LIMIT,
    getExceededText: ({ tokenCount, recommendedLength }) =>
      getString("submitReview.exceedString", { recommendedLength }),
  });

  const {
    footerText: reviewTitleFooterText,
    progress: reviewTitleProgress,
    count: reviewTitleCount,
  } = useRecommendedLength({
    value: reviewTitle,
    tokenType: TokenType.Words,
    recommendedLength: REVIEW_TITLE_WORD_LIMIT,
    getExceededText: ({ tokenCount, recommendedLength }) =>
      getString("submitReview.exceedString", { recommendedLength }),
  });

  // Review title does not exceed limit
  useEffect(() => {
    if (reviewTitleCount > REVIEW_TITLE_WORD_LIMIT) {
      setReviewTitleError(true);
    } else {
      setReviewTitleError(false);
    }
  }, [reviewTitleCount]);

  // Review content does not exceed limit
  useEffect(() => {
    if (reviewContentCount > REVIEW_CONTENT_WORD_LIMIT) {
      setReviewContentError(true);
    } else {
      setReviewContentError(false);
    }
  }, [reviewContentCount]);

  // Enable submit once all values are filled
  useEffect(() => {
    if (isFormValid()) {
      setEnableConfirmButton(true);
    } else {
      setEnableConfirmButton(false);
    }
  }, [selectedCategories, reviewTitle, reviewContent, reviewTitleError, reviewContentError]);

  const close = () => {
    setIsOpen(false);
  };

  const isFormValid = () => {
    return (
      selectedCategories.length > 0 &&
      reviewTitle.length > 0 &&
      reviewContent.length > 0 &&
      !reviewTitleError &&
      !reviewContentError
    );
  };

  const buttonCancel = (
    <Button onClick={close} variant={ButtonVariant.Secondary} dataTestId="submit-review-button-cancel">
      {getString("submitReview.cancelButton")}
    </Button>
  );

  const buttonSubmit = (
    <Button
      onClick={() => {}}
      variant={ButtonVariant.Primary}
      dataTestId="submit-review-button-submit"
      disabled={!enableConfirmButton}
    >
      {getString("submitReview.submitButton")}
    </Button>
  );

  const renderCategories = () => {
    return CATEGORIES.map((category, index) => {
      return (
        <Chip
          key={`category-chip-${index}`}
          data-test-id={`${category}-chip`}
          isSelected={selectedCategories.includes(index)}
          onClick={() => {
            if (selectedCategories.includes(index)) {
              setSelectedCategories(selectedCategories.filter((chip) => chip !== index));
            } else {
              setSelectedCategories([...selectedCategories, index]);
            }
          }}
        >
          {category}
        </Chip>
      );
    });
  };

  const renderRating = () => {
    return (
      <Col>
        <Label style={{ marginBottom: "8px" }}>{getString("submitReview.yourRatingLabel")}</Label>
        <StarRating
          name="submit-review-star-rating"
          data-test-id="submit-review-star-rating"
          value={previousRating}
          onChange={() => {}}
        />
      </Col>
    );
  };

  const renderReviewTitle = () => {
    return (
      <InputWrapper
        id="submit-review-title-input"
        dataTestId="submit-review-title-input"
        labelText={getString("submitReview.reviewTitleLabel")}
        error={reviewTitleError}
        footer={(footerProps) =>
          reviewTitleError && (
            <RecommendedLengthFooter progress={reviewTitleProgress} {...footerProps}>
              {reviewTitleFooterText}
            </RecommendedLengthFooter>
          )
        }
      >
        {(inputProps) => (
          <TextArea
            {...inputProps}
            name="submit-review-title-input"
            data-test-id="submit-review-title-input"
            placeholder={getString("submitReview.reviewTitlePlaceholder")}
            value={reviewTitle}
            onChange={(e) => setReviewTitle(e.target.value)}
          />
        )}
      </InputWrapper>
    );
  };

  const renderReviewContent = () => {
    return (
      <InputWrapper
        id="submit-review-content-input"
        dataTestId="submit-review-content-input"
        labelText={getString("submitReview.reviewContentLabel")}
        error={reviewContentError}
        footer={(footerProps) => (
          <RecommendedLengthFooter progress={reviewContentProgress} {...footerProps}>
            {reviewContentFooterText}
          </RecommendedLengthFooter>
        )}
      >
        {(inputProps) => (
          <TextArea
            {...inputProps}
            name="submit-review-content-input"
            data-test-id="submit-review-content-input"
            placeholder={getString("submitReview.reviewContentPlaceholder")}
            resize="vertical"
            height={"314px"}
            value={reviewContent}
            onChange={(e) => setReviewContent(e.target.value)}
          />
        )}
      </InputWrapper>
    );
  };

  const renderReviewCategories = () => {
    return (
      <Col>
        <Label style={{ marginBottom: "8px" }}>{getString("submitReview.reviewCategoriesLabel")}</Label>
        <Row gridGap={"12px"} flexWrap="wrap" rowGap={"16px"} dataTestId="submit-review-categories">
          {renderCategories()}
        </Row>
      </Col>
    );
  };

  return (
    <Flyout isOpen={isOpen} close={close} dataTestId="submit-review-form">
      <FlyoutContent onCloseButtonClick={close} titleText={titleText} buttons={[buttonCancel, buttonSubmit]}>
        <Col width={"544px"} rowGap={"20px"}>
          <Text fontWeight={"bold"}>{productName}</Text>
          {renderRating()}
          {renderReviewTitle()}
          {renderReviewContent()}
          {renderReviewCategories()}
        </Col>
      </FlyoutContent>
    </Flyout>
  );
};
